// Aceptación del documento tributario CON registro de sus datos impresos (emisor, RUT,
// folio, fechas, monto). El registro no es burocracia: (RUT emisor, folio) es la llave
// con la que el backend detecta documentos repetidos — el mismo PDF o el mismo folio
// subido en otra semana u otra cuenta. Por eso RUT y folio son obligatorios, y por eso
// el 409 del backend se muestra DENTRO del modal (dice exactamente dónde está el
// duplicado) en vez de perderse en un toast.
//
// El operador además CONFIRMA el tipo (factura/boleta): la semana se paga con el tipo del
// documento, así que el backend cruza el monto declarado contra el esperado para ese tipo.
// Si no cuadra (409 'amount_mismatch') el modal muestra declarado vs esperado y deja
// aceptar de todos modos solo con un motivo, que queda registrado con la aceptación.
import React, { useEffect, useRef, useState } from 'react';
import { toast } from '../../../utils/toast';
import { useTranslation } from 'react-i18next';
import { AlertTriangle, CheckCircle } from 'lucide-react';
import { Button } from '../../../components/ui/Button';
import { Input } from '../../../components/ui/Input';
import { Modal } from '../../../components/ui/Modal';
import { Select } from '../../../components/ui/Select';
import { Textarea } from '../../../components/ui/Textarea';
import { acceptDriverBillingDocument } from '../../../api/billing';
import { formatCurrency, formatDate } from './utils/cobroHelpers';
import { RUT_STATE, rutErrorKey, validateRut } from '../../../utils/rut';
import { BILLING_DOC_OPTIONS, billingDocLabel } from '../../../utils/billingDocTypes';
import {
  apiErrorCode,
  apiErrorData,
  apiErrorMessage,
  apiErrorStatus,
} from '../../../utils/apiErrorMessage';

const EMPTY = {
  emisorNombre: '',
  emisorRut: '',
  folio: '',
  fechaDocumento: '',
  fechaEmision: '',
  montoTotal: '',
};

// Tolerancia de redondeo con la que billing da por cuadrado el monto (±2 CLP).
const AMOUNT_TOLERANCE = 2;
// Largo del motivo para aceptar con monto distinto (mismo rango que valida el backend).
const REASON_MIN = 3;
const REASON_MAX = 300;

const isDocType = (value) => BILLING_DOC_OPTIONS.some((o) => o.value === value);
const otherDocType = (value) => (value === 'factura' ? 'boleta' : 'factura');
const finiteOrNull = (value) => (value != null && Number.isFinite(Number(value)) ? Number(value) : null);

export default function AcceptDocumentModal({ open, target, onClose, onAccepted }) {
  const { t } = useTranslation();
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const [rutTouched, setRutTouched] = useState(false);
  // Mensaje del 409 (documento repetido): vive en el modal porque trae el dato que el
  // operador necesita para resolver (en qué semana y de quién está el folio duplicado).
  const [conflict, setConflict] = useState(null);
  // Tipo que confirma el operador; se envía como `docType`.
  const [docType, setDocType] = useState('');
  // 409 'amount_mismatch': { docType, declared, expected: { factura, boleta } }.
  const [mismatch, setMismatch] = useState(null);
  const [overrideReason, setOverrideReason] = useState('');
  // 400 'amount_required': el monto es obligatorio para aceptar.
  const [amountError, setAmountError] = useState(null);
  // 400 'invalid_doc_type': el tipo enviado no es factura ni boleta.
  const [docTypeError, setDocTypeError] = useState(null);
  const reasonRef = useRef(null);

  const doc = target?.doc;
  // Fila del summary (opcional): su tipo efectivo prellena documentos viejos sin docType.
  const row = target?.d;
  // Esperado por tipo ({ factura, boleta } | null) que calcula la vista SOLO cuando es
  // comparable (rango que cubre la semana del documento y retiros leídos). Si no viene,
  // no se muestra esperado local: el 409 del backend sigue mandando.
  const expected = target?.expected ?? null;

  // Al abrir: prefill desde lo ya registrado (docMeta) para corregir sin re-tipear.
  useEffect(() => {
    if (!open) return;
    const m = doc?.docMeta;
    setConflict(null);
    setMismatch(null);
    setOverrideReason('');
    setAmountError(null);
    setDocTypeError(null);
    setRutTouched(false);
    // El tipo congelado del documento; si un documento viejo no lo trae, el efectivo de la fila.
    setDocType(isDocType(doc?.docType) ? doc.docType : isDocType(row?.billingDocType) ? row.billingDocType : '');
    setForm(
      m
        ? {
            emisorNombre: m.emisorNombre ?? '',
            emisorRut: m.emisorRut ?? '',
            folio: m.folio ?? '',
            fechaDocumento: m.fechaDocumento ?? '',
            fechaEmision: m.fechaEmision ?? '',
            montoTotal: m.montoTotal != null ? String(m.montoTotal) : '',
          }
        : EMPTY,
    );
  }, [open, doc, row]);

  // Con el paso "aceptar de todos modos" abierto, el foco va al motivo (lo que falta).
  useEffect(() => {
    if (mismatch) reasonRef.current?.focus();
  }, [mismatch]);

  const set = (k) => (e) => {
    setForm((f) => ({ ...f, [k]: e.target.value }));
    // Cambiar el monto invalida la comparación del backend: se vuelve a verificar.
    if (k === 'montoTotal') {
      setMismatch(null);
      setAmountError(null);
    }
  };

  const changeDocType = (e) => {
    setDocType(e.target.value);
    setMismatch(null);
    setDocTypeError(null);
  };

  // El RUT del emisor es la llave con la que el backend detecta documentos repetidos
  // (RUT, folio): si entra mal, el control de duplicados deja de servir. Por eso el
  // formulario no deja registrar mientras no pase el módulo 11, y el mensaje dice qué
  // dígito verificador corresponde en vez de un "inválido" a secas.
  const rutCheck = validateRut(form.emisorRut);
  // El DV equivocado se avisa al tiro (es definitivo); "incompleto" solo después de salir
  // del campo, para no marcar en rojo a alguien que todavía está escribiendo.
  const rutError =
    rutCheck.state === RUT_STATE.BAD_DV || (rutTouched && rutCheck.state === RUT_STATE.INCOMPLETE)
      ? rutErrorKey(rutCheck)
      : null;
  const reasonLength = overrideReason.trim().length;
  const reasonOk = reasonLength >= REASON_MIN && reasonLength <= REASON_MAX;
  // El tipo es obligatorio: con él billing decide cómo se paga la semana y cuánto debe decir.
  const canSubmit = rutCheck.ok && form.folio.trim() !== '' && isDocType(docType) && (!mismatch || reasonOk);
  const RUT_ERROR_ID = 'doc-meta-rut-error';
  const AMOUNT_ERROR_ID = 'doc-meta-monto-error';
  const DOC_TYPE_ERROR_ID = 'doc-meta-tipo-error';

  // Vista previa local (antes de enviar): monto escrito vs esperado para el tipo elegido.
  const montoNum = form.montoTotal.trim() !== '' ? finiteOrNull(form.montoTotal) : null;
  const expectedForType = expected && isDocType(docType) ? expected[docType] : null;
  const previewDiff = montoNum !== null && expectedForType !== null ? montoNum - expectedForType : null;
  const previewMatchesOther =
    previewDiff !== null &&
    Math.abs(previewDiff) > AMOUNT_TOLERANCE &&
    Math.abs(montoNum - expected[otherDocType(docType)]) <= AMOUNT_TOLERANCE;

  // Datos del 409: SOLO los del backend (el esperado local puede no ser comparable). Si el
  // 409 no trae el esperado del tipo, se muestra su mensaje en vez de inventar cifras.
  const mmType = isDocType(mismatch?.docType) ? mismatch.docType : docType;
  const mmDeclared = finiteOrNull(mismatch?.declared) ?? montoNum;
  const mmExpected = {
    factura: finiteOrNull(mismatch?.expected?.factura),
    boleta: finiteOrNull(mismatch?.expected?.boleta),
  };
  const mmHasExpected = isDocType(mmType) && mmExpected[mmType] !== null;
  const mmOther = isDocType(mmType) ? otherDocType(mmType) : null;
  const mmMatchesOther =
    mmOther !== null &&
    mmDeclared !== null &&
    mmExpected[mmOther] !== null &&
    Math.abs(mmDeclared - mmExpected[mmOther]) <= AMOUNT_TOLERANCE;

  const submit = async () => {
    if (!canSubmit || !target) return;
    setBusy(true);
    setConflict(null);
    setAmountError(null);
    try {
      const monto = Number(form.montoTotal);
      await acceptDriverBillingDocument({
        driverId: target.driverId,
        weekStart: doc.weekStart,
        meta: {
          emisorNombre: form.emisorNombre.trim() || undefined,
          emisorRut: form.emisorRut.trim(),
          folio: form.folio.trim(),
          fechaDocumento: form.fechaDocumento || undefined,
          fechaEmision: form.fechaEmision || undefined,
          montoTotal: form.montoTotal.trim() !== '' && Number.isFinite(monto) ? monto : undefined,
        },
        docType: isDocType(docType) ? docType : undefined,
        amountOverrideReason: mismatch && reasonOk ? overrideReason.trim() : undefined,
      });
      toast.success(t('Documento aceptado'));
      onAccepted?.();
      onClose?.();
    } catch (err) {
      const status = apiErrorStatus(err);
      const code = String(apiErrorCode(err) ?? '').toLowerCase();
      if (status === 409 && code === 'amount_mismatch') {
        const details = apiErrorData(err)?.details;
        setMismatch({
          ...(details && typeof details === 'object' ? details : {}),
          message: apiErrorMessage(err, null),
        });
        setOverrideReason('');
      } else if (status === 400 && code === 'invalid_doc_type') {
        setDocTypeError(apiErrorMessage(err, t('Elige si el documento es factura o boleta.')));
      } else if (status === 400 && code === 'amount_required') {
        setAmountError(t('Falta el monto total. Cópialo del documento: sin él no se puede verificar que cuadre.'));
      } else if (status === 409) {
        // 409 sin código: documento repetido (RUT, folio) — el mensaje dice dónde está.
        setConflict(apiErrorMessage(err, t('Documento repetido')));
      } else {
        toast.error(apiErrorMessage(err, t('No se pudo aceptar el documento')));
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={() => { if (!busy) onClose?.(); }}
      size="md"
      title={t('Aceptar documento')}
      description={t('{{name}} · semana {{from}} al {{to}}. Registra los datos impresos del documento para validar que no esté repetido.', {
        name: target?.driverName ?? '',
        from: formatDate(doc?.weekStart ?? ''),
        to: formatDate(doc?.weekEnd ?? ''),
      })}
      footer={
        <>
          <Button variant="outline" onClick={onClose} disabled={busy}>
            {t('Cancelar')}
          </Button>
          <Button
            onClick={submit}
            loading={busy}
            disabled={!canSubmit}
            iconLeft={<CheckCircle className="w-4 h-4" />}
          >
            {mismatch ? t('Aceptar de todos modos') : t('Registrar y aceptar')}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-3">
        {conflict && (
          <div className="text-sm text-status-error" role="alert">
            {conflict}
          </div>
        )}
        {mismatch && (
          <div className="flex flex-col gap-2.5 rounded-lg bg-status-warning-soft px-3.5 py-3 text-sm">
            <p role="alert" className="m-0 flex items-start gap-2 font-medium text-status-warning">
              <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" aria-hidden />
              {!mmHasExpected
                ? mismatch.message || t('El monto declarado no cuadra con lo esperado.')
                : t('El monto declarado no cuadra con lo esperado para {{tipo}}.', {
                    tipo: billingDocLabel(mmType, t).toLowerCase(),
                  })}
            </p>
            {mmHasExpected && (
              <dl className="m-0 grid grid-cols-3 gap-x-4 text-xs">
                <div className="flex flex-col gap-0.5">
                  <dt className="text-fg-muted">{t('Declarado')}</dt>
                  <dd className="m-0 font-medium text-fg tabular-nums">
                    {mmDeclared !== null ? formatCurrency(mmDeclared) : '—'}
                  </dd>
                </div>
                <div className="flex flex-col gap-0.5">
                  <dt className="text-fg-muted">{t('Esperado como factura')}</dt>
                  <dd className={`m-0 tabular-nums ${mmType === 'factura' ? 'font-medium text-fg' : 'text-fg-muted'}`}>
                    {mmExpected.factura !== null ? formatCurrency(mmExpected.factura) : '—'}
                  </dd>
                </div>
                <div className="flex flex-col gap-0.5">
                  <dt className="text-fg-muted">{t('Esperado como boleta')}</dt>
                  <dd className={`m-0 tabular-nums ${mmType === 'boleta' ? 'font-medium text-fg' : 'text-fg-muted'}`}>
                    {mmExpected.boleta !== null ? formatCurrency(mmExpected.boleta) : '—'}
                  </dd>
                </div>
              </dl>
            )}
            {mmHasExpected && mmMatchesOther && (
              <p className="m-0 text-xs text-fg">
                {t('El monto cuadra con {{tipo}}: si el documento es {{tipo}}, corrige el tipo en vez de aceptar con diferencia.', {
                  tipo: billingDocLabel(mmOther, t).toLowerCase(),
                })}
              </p>
            )}
            <div className="bill-field">
              <label className="bill-label" htmlFor="doc-accept-override-reason">
                {t('Motivo para aceptar de todos modos *')}
              </label>
              <Textarea
                id="doc-accept-override-reason"
                ref={reasonRef}
                rows={2}
                value={overrideReason}
                onChange={(e) => setOverrideReason(e.target.value)}
                maxLength={REASON_MAX}
                placeholder={t('Ej: descuento acordado con el repartidor')}
                aria-describedby="doc-accept-override-hint"
              />
              <span id="doc-accept-override-hint" className="text-xs text-fg-muted tabular-nums">
                {t('Queda registrado con la aceptación. {{n}}/{{max}} caracteres (mínimo {{min}}).', {
                  n: reasonLength,
                  max: REASON_MAX,
                  min: REASON_MIN,
                })}
              </span>
            </div>
          </div>
        )}
        <div className="bill-field">
          <label className="bill-label" htmlFor="doc-meta-emisor">{t('Emisor (razón social)')}</label>
          <Input
            id="doc-meta-emisor"
            value={form.emisorNombre}
            onChange={set('emisorNombre')}
            placeholder={t('Ej: HVC Transporte SpA')}
            maxLength={200}
          />
        </div>
        <div className="flex gap-3 flex-wrap">
          <div className="bill-field" style={{ flex: 1, minWidth: 160 }}>
            <label className="bill-label" htmlFor="doc-meta-rut">{t('RUT del emisor *')}</label>
            <Input
              id="doc-meta-rut"
              value={form.emisorRut}
              onChange={set('emisorRut')}
              onBlur={() => setRutTouched(true)}
              placeholder="77.576.838-K"
              maxLength={20}
              invalid={!!rutError}
              aria-invalid={!!rutError}
              aria-describedby={rutError ? RUT_ERROR_ID : undefined}
            />
            {rutError && (
              <p id={RUT_ERROR_ID} className="text-xs text-status-error mt-1" role="alert">
                {t(rutError, { dv: rutCheck.expectedDv })}
              </p>
            )}
          </div>
          <div className="bill-field" style={{ flex: 1, minWidth: 140 }}>
            <label className="bill-label" htmlFor="doc-meta-folio">{t('N° de documento *')}</label>
            <Input
              id="doc-meta-folio"
              value={form.folio}
              onChange={set('folio')}
              placeholder={t('Folio, ej: 101')}
              maxLength={30}
            />
          </div>
        </div>
        <div className="flex gap-3 flex-wrap">
          <div className="bill-field" style={{ flex: 1, minWidth: 150 }}>
            <label className="bill-label" htmlFor="doc-meta-fecha">{t('Fecha del documento')}</label>
            <Input
              id="doc-meta-fecha"
              type="date"
              value={form.fechaDocumento}
              onChange={set('fechaDocumento')}
            />
          </div>
          <div className="bill-field" style={{ flex: 1, minWidth: 150 }}>
            <label className="bill-label" htmlFor="doc-meta-emision">{t('Fecha de emisión')}</label>
            <Input
              id="doc-meta-emision"
              type="date"
              value={form.fechaEmision}
              onChange={set('fechaEmision')}
            />
          </div>
        </div>
        <div className="flex gap-3 flex-wrap">
          <div className="bill-field" style={{ flex: 1, minWidth: 150 }}>
            <label className="bill-label" htmlFor="doc-meta-tipo">{t('Tipo de documento')}</label>
            <Select
              id="doc-meta-tipo"
              value={docType}
              onChange={changeDocType}
              placeholder={docType ? undefined : t('Elegir tipo')}
              options={BILLING_DOC_OPTIONS.map((o) => ({ value: o.value, label: t(o.label) }))}
              error={!!docTypeError}
              aria-invalid={!!docTypeError}
              aria-describedby={docTypeError ? DOC_TYPE_ERROR_ID : undefined}
            />
            {docTypeError && (
              <p id={DOC_TYPE_ERROR_ID} className="text-xs text-status-error mt-1" role="alert">
                {docTypeError}
              </p>
            )}
          </div>
          <div className="bill-field" style={{ flex: 1, minWidth: 150 }}>
            <label className="bill-label" htmlFor="doc-meta-monto">{t('Monto total (CLP)')}</label>
            <Input
              id="doc-meta-monto"
              type="number"
              value={form.montoTotal}
              onChange={set('montoTotal')}
              placeholder={t('Ej: 483735')}
              invalid={!!amountError}
              aria-invalid={!!amountError}
              aria-describedby={amountError ? AMOUNT_ERROR_ID : undefined}
            />
            {amountError && (
              <p id={AMOUNT_ERROR_ID} className="text-xs text-status-error mt-1" role="alert">
                {amountError}
              </p>
            )}
          </div>
        </div>
        {/* Lo que debería decir el documento según cada tipo (misma regla que billing al
            aceptar). Se muestran los dos: el operador está eligiendo entre ellos. */}
        {expected && (
          <p className="m-0 -mt-1 text-xs text-fg-muted tabular-nums">
            {t('Esperado')}:{' '}
            <span className={docType === 'factura' ? 'font-medium text-fg' : undefined}>
              {t('Factura')} {formatCurrency(expected.factura)}
            </span>
            {' · '}
            <span className={docType === 'boleta' ? 'font-medium text-fg' : undefined}>
              {t('Boleta')} {formatCurrency(expected.boleta)}
            </span>
            {!mismatch && previewDiff !== null && Math.abs(previewDiff) > AMOUNT_TOLERANCE && (
              <span className="block mt-0.5 text-status-warning">
                {previewMatchesOther
                  ? t('El monto escrito cuadra con {{tipo}}: revisa el tipo de documento.', {
                      tipo: billingDocLabel(otherDocType(docType), t).toLowerCase(),
                    })
                  : t('El monto escrito difiere en {{monto}} de lo esperado.', {
                      monto: formatCurrency(Math.abs(previewDiff)),
                    })}
              </span>
            )}
          </p>
        )}
      </div>
    </Modal>
  );
}
